// @flow

import React, {Fragment} from 'react';
import {createSelector} from 'reselect';
import {connect} from 'react-redux';
import moment from 'moment-tz';

import {
  getObjectReviews,
  markAsReviewed,
  setNextReviewAt,
} from 'app/review/actions';
import {objectsToFlatArray} from 'app/utils/redux';
import {gt} from 'app/utils/i18n';
import {getContentType} from 'app/utils/content-type';
import {SUPPORT_REMINDER_URL} from 'app/urls';
import {keepUpdatedEnhancer} from 'app/general/keepUpdatedEnhancer';
import {
  runWhenAllRelatedMatch,
  type ModelSubscribedComponentPropsType,
  type ModelChangeMessageType,
} from 'app/subscriptions/index';
import StateButton from 'app/general/widgets/StateButton';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {
  CloseURLSidebarPagePanels,
  StyledFooterDIV,
} from 'app/general/structures/DocSidebarPagePanels';
import {CurrentUserSelector} from 'app/current-user/api';
import {accountSettingsSelector} from 'app/settings/api';
import DetailPageReviewSidebarItem from './DetailPageReviewSidebarItem';
import {getNextReviewDate} from 'app/review/utils';
import {ReviewMeta} from 'app/review/components/ReviewMeta';
import {createFormErrorsContext, type FormErrorsProps} from 'app/general/forms';
import type {ReviewType, VersionedReviewableObject} from 'app/review/types';
import type {ProcedureType} from 'app/procedures/types';
import type {PolicyType} from 'app/policies/types';
import type {ProcessType} from 'app/process/types';
import {type StoreShapeType} from 'app/reducers/types';

export const DetailPageReviewSidebarFactory = <
  T: VersionedReviewableObject
>() => {
  type OwnProps = {|
    title: string,
    canReview: boolean,
    object: T,
    contentType: 'policy' | 'procedure' | 'proceduregroup',
    versionId: number,
    versionContentType: 'policyversion' | 'procedureversion' | 'processversion',
    onReview: () => any,
    onToggleReview: () => any,
  |};

  type ModelSubscribedOwnProps = {|
    ...ModelSubscribedComponentPropsType,
    ...OwnProps,
  |};

  type StateProps = {|
    reviews: Array<ReviewType>,
    moreToLoad: boolean,
    userTimezone: string,
    nextReviewDate: string,
    defaultReviewPeriod: number,
  |};

  type DispatchProps = {|
    getObjectReviews: (
      objectId: number,
      contentType: 'policy' | 'procedure' | 'proceduregroup',
      loadNextPage?: boolean
    ) => Promise<any>,
    markAsReviewed: (
      versionId: number,
      versionContentType:
        | 'policyversion'
        | 'procedureversion'
        | 'processversion'
    ) => Promise<any>,
    setNextReviewAt: (
      documentURL: string,
      next_review_at: string | null,
      review_period_months: number | null,
      reviewer: string | null
    ) => Promise<any>,
  |};

  type ConnectedProps = {|
    ...ModelSubscribedOwnProps,
    ...StateProps,
    ...DispatchProps,
  |};

  type Props = {
    ...ConnectedProps,
    ...FormErrorsProps,
  };

  type State = {
    loading: boolean,
    isEditingReview: boolean,
  };

  class GenericDetailPageReviewSidebar extends React.PureComponent<
    Props,
    State
  > {
    state = {loading: true, isEditingReview: false};

    constructor(props) {
      super(props);
      this.props.onModelChange(this.handleModelChange);
    }

    UNSAFE_componentWillMount() {
      this.fetchData();
    }

    handleModelChange = (message: ModelChangeMessageType) => {
      const {contentType, object} = this.props;

      runWhenAllRelatedMatch(
        message,
        'review',
        {
          content_type: contentType,
          id: object.id,
        },
        () => this.fetchData()
      );
    };

    disableLoading = () => this.setState({loading: false});
    setIsEditingReview = (isEditing) =>
      this.setState({isEditingReview: isEditing});

    fetchData = (loadNextPage?: boolean = false) => {
      this.setState({loading: true});
      const {object, contentType} = this.props;

      return this.props
        .getObjectReviews(object.id, contentType, loadNextPage)
        .then(this.disableLoading, this.disableLoading);
    };

    markAsReviewed = () =>
      this.props
        .markAsReviewed(this.props.versionId, this.props.versionContentType)
        .then(() => this.props.onReview());

    toggleReview = (
      nextReviewDate: string | null,
      reviewPeriodMonths: number | null,
      reviewer: string | null
    ): Promise<any> => {
      const {
        setNextReviewAt,
        object,
        onToggleReview,
        captureFormErrorsFromRejection,
      } = this.props;

      return setNextReviewAt(
        object.url,
        nextReviewDate,
        reviewPeriodMonths,
        reviewer
      ).then(
        () => onToggleReview(),
        (err) => {
          captureFormErrorsFromRejection(err);
          throw err;
        }
      );
    };

    render() {
      const {
        reviews,
        title,
        canReview,
        contentType,
        userTimezone,
        object,
        moreToLoad,
      } = this.props;
      const {loading, isEditingReview} = this.state;

      const ct = getContentType(contentType);
      const enabled = object.next_review_at != null;

      return (
        <CloseURLSidebarPagePanels
          title={title}
          closeUrl={object.html_url}
          loading={this.state.loading}
          additionalHeader={
            <ReviewMeta
              toggleReview={this.toggleReview}
              teamIds={object.team_names.map((team) => team.id)}
              enabled={enabled}
              isEditing={isEditingReview}
              setEditing={this.setIsEditingReview}
              reviewer={object.reviewer}
              nextReviewAt={
                object.next_review_at != null
                  ? object.next_review_at
                  : this.props.nextReviewDate
              }
              reviewPeriodMonths={
                object.review_period_months != null
                  ? object.review_period_months
                  : this.props.defaultReviewPeriod
              }
            />
          }
          footer={
            canReview && enabled ? (
              <StyledFooterDIV>
                <button
                  disabled={isEditingReview}
                  className="btn btn-secondary btn-sm btn-block"
                  onClick={this.markAsReviewed}
                >
                  <i className="fa fa-certificate" /> {gt('Mark as Reviewed')}
                </button>
              </StyledFooterDIV>
            ) : null
          }
        >
          {reviews.length === 0 && !loading ? (
            <NoResultsMessage
              icon="fa-certificate"
              head={
                enabled
                  ? gt('This %s has not been reviewed', ct.name)
                  : gt('Reviews are disabled')
              }
              onSidebar={true}
            >
              {enabled ? (
                canReview ? (
                  <p>{gt('Use the button below when completing a review')}</p>
                ) : (
                  ''
                )
              ) : canReview ? (
                <p>
                  {gt(
                    'Use the button above to enable reviews for this %s',
                    ct.name
                  )}
                </p>
              ) : (
                ''
              )}
              <p>
                <a href={SUPPORT_REMINDER_URL}>
                  {canReview
                    ? gt('Or read our help article about reviews')
                    : gt('Read our help article about reviews')}
                </a>
              </p>
            </NoResultsMessage>
          ) : (
            <Fragment>
              <ul className="list-unstyled">
                {reviews.map((review) => (
                  <li key={review.url} className="p-0">
                    <DetailPageReviewSidebarItem
                      review={review}
                      reviewedObject={object}
                      userTimezone={userTimezone}
                    />
                  </li>
                ))}
              </ul>
              {moreToLoad && (
                <div className="text-center my-2 mx-4">
                  <StateButton
                    className="btn btn-secondary btn-sm btn-block"
                    busyText={gt('Loading...')}
                    onClick={() => this.fetchData(true)}
                  >
                    {gt('Load more')}
                  </StateButton>
                </div>
              )}
            </Fragment>
          )}
        </CloseURLSidebarPagePanels>
      );
    }
  }

  const mapStateToProps = createSelector<
    StoreShapeType,
    ModelSubscribedOwnProps,
    StateProps,
    any,
    any,
    any,
    any,
    any
  >(
    (state, {object, contentType}) =>
      state.reviews.getIn(['objects', contentType, object.id.toString()]),
    (state, {object, contentType}) =>
      state.reviews.getIn(
        ['objects', contentType, object.id.toString(), 'next'],
        false
      ),
    (state) => CurrentUserSelector(state).data.timezone,
    (state) => accountSettingsSelector(state).data.reviews,
    (reviews, moreToLoad, userTimezone, {default_interval, default_time}) => ({
      reviews: reviews != null ? objectsToFlatArray(reviews) : [],
      moreToLoad,
      userTimezone,
      nextReviewDate: getNextReviewDate(
        default_interval,
        default_time,
        userTimezone
      ),
      defaultReviewPeriod: moment.duration(default_interval).asMonths(),
    })
  );

  return keepUpdatedEnhancer<OwnProps>(
    connect<
      ConnectedProps,
      ModelSubscribedOwnProps,
      StateProps,
      DispatchProps,
      StoreShapeType,
      _
    >(
      mapStateToProps,
      // $FlowFixMe
      {getObjectReviews, markAsReviewed, setNextReviewAt}
    )(createFormErrorsContext<ConnectedProps>(GenericDetailPageReviewSidebar))
  );
};

export const ProcedureDetailPageReviewSidebar =
  DetailPageReviewSidebarFactory<ProcedureType>();
export const PolicyDetailPageReviewSidebar =
  DetailPageReviewSidebarFactory<PolicyType>();
export const ProcessDetailPageReviewSidebar =
  DetailPageReviewSidebarFactory<ProcessType>();
